<template>
    <section class="relative w-full overflow-hidden bg-primary-900">
        <!-- Background -->
        <img v-if="backgroundImage" :src="backgroundImage" alt=""
            class="absolute inset-0 h-full w-full object-cover" />
        <div class="absolute inset-0 bg-primary-900/60"></div>
        <div class="absolute inset-0 bg-[radial-gradient(120%_120%_at_50%_0%,rgb(var(--core-primary-600)/0.6)_0%,rgb(var(--core-primary-800)/0.88)_55%,rgb(var(--core-primary-900)/0.95)_100%)]">
        </div>

        <!-- Content -->
        <div class="relative z-10 mx-auto flex min-h-[68vh] max-w-5xl flex-col items-center justify-center px-5 py-24 text-center sm:min-h-[72vh] sm:py-28">
            <!-- Countdown -->
            <div class="flex items-start justify-center gap-6 sm:gap-10">
                <div v-for="unit in units" :key="unit.key" class="flex flex-col items-center">
                    <span class="text-5xl font-extrabold leading-none tabular-nums sm:text-6xl lg:text-7xl"
                        :class="unit.color">
                        {{ pad(unit.value) }}
                    </span>
                    <span class="mt-2 text-xs font-bold uppercase tracking-wide sm:text-sm" :class="unit.color">
                        {{ unit.label }}
                    </span>
                </div>
            </div>

            <!-- Title -->
            <h2 class="mt-12 text-3xl font-extrabold uppercase leading-tight text-white sm:text-5xl lg:text-6xl">
                <template v-if="edition">
                    {{ edition }}<sup class="text-[0.5em] font-bold">{{ ordinal(edition) }}</sup>
                </template>
                {{ title }}
            </h2>

            <!-- Subtitle -->
            <p v-if="subtitle" class="mt-5 text-base font-bold uppercase tracking-wide text-white/85 sm:text-xl">
                {{ subtitle }}
            </p>

            <!-- Actions -->
            <div class="mt-9 flex flex-wrap items-center justify-center gap-4">
                <NuxtLink :to="registerTarget"
                    class="inline-flex items-center rounded-full bg-primary-500 px-8 py-3 text-sm font-semibold text-on-primary shadow-lg transition-colors hover:bg-primary-600">
                    Register Now
                </NuxtLink>
                <NuxtLink :to="detailTarget"
                    class="inline-flex items-center rounded-full bg-white px-8 py-3 text-sm font-semibold text-primary-800 shadow-lg transition-colors hover:bg-white/90">
                    Learn More
                </NuxtLink>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { EventItem } from '~/types';

const props = withDefaults(defineProps<{
    /** Event to count down to; its startDate is used as the target. */
    event?: EventItem | null;
    /** Alias for `event` (backwards compatible). */
    item?: EventItem | null;
    /** Explicit target date (ISO). Overrides the event startDate when set. */
    target?: string;
    /** Edition number rendered with an ordinal suffix, e.g. 25 -> 25th. */
    edition?: number | null;
    /** Heading text. Falls back to the event title. */
    heading?: string;
    /** Sub-heading. Falls back to "<date> IN <location>". */
    tagline?: string;
    /** Background image. Falls back to the event's imageUrl. */
    backgroundUrl?: string;
}>(), {
    event: null,
    item: null,
    target: '',
    edition: null,
    heading: '',
    tagline: '',
    backgroundUrl: '',
});

const evt = computed(() => props.event || props.item);

const backgroundImage = computed(() => props.backgroundUrl || evt.value?.imageUrl || '');

const targetDate = computed(() => {
    const iso = props.target || evt.value?.startDate || '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
});

const title = computed(() => props.heading || evt.value?.title || 'Coming Soon');

const subtitle = computed(() => {
    if (props.tagline) return props.tagline;
    const d = targetDate.value;
    const parts: string[] = [];
    if (d) {
        parts.push(
            d.toLocaleDateString('default', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase()
        );
    }
    if (evt.value?.location) parts.push('IN ' + evt.value.location.toUpperCase());
    return parts.join(' ');
});

// --- countdown ---
const now = ref(useRenderTime());
let timer: ReturnType<typeof setInterval> | null = null;

const remaining = computed(() => {
    if (!targetDate.value) return 0;
    return Math.max(0, targetDate.value.getTime() - now.value);
});

const units = computed(() => {
    const total = Math.floor(remaining.value / 1000);
    return [
        { key: 'd', label: 'Days', color: 'text-white', value: Math.floor(total / 86400) },
        { key: 'h', label: 'Hours', color: 'text-white', value: Math.floor((total % 86400) / 3600) },
        { key: 'm', label: 'Minutes', color: 'text-white', value: Math.floor((total % 3600) / 60) },
        { key: 's', label: 'Seconds', color: 'text-white', value: total % 60 },
    ];
});

const pad = (n: number) => String(n).padStart(2, '0');

const ordinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return (s[(v - 20) % 10] || s[v] || s[0]).toUpperCase();
};

const start = () => {
    stop();
    now.value = Date.now();
    timer = setInterval(() => { now.value = Date.now(); }, 1000);
};
const stop = () => {
    if (timer) clearInterval(timer);
    timer = null;
};

onMounted(start);
onBeforeUnmount(stop);
watch(targetDate, () => { now.value = Date.now(); });

// --- routing ---
const detailTarget = computed(() => evt.value
    ? { name: 'EventSingle', params: { slug: evt.value.slug || evt.value.id } }
    : { name: 'ListOfEvents' });
const registerTarget = computed(() => evt.value
    ? { name: 'EventSingle', params: { slug: evt.value.slug || evt.value.id }, query: { section: 'tickets' } }
    : { name: 'ListOfEvents' });
</script>
